/**
 * @module features/perf/perfPresentation
 *
 * Verdict and status badges and run-list helpers. Row formatting (value, delta, interval)
 * comes from `@chatsdet/types/perf` so the page and the CLI output agree.
 */
import type { TagColor } from '@chatsdet/ui';
import { signed, unitSuffix } from '@chatsdet/types/perf';
import type { PerfRow, PerfRunHeadlineDelta, PerfRunStatus, PerfVerdict } from '@chatsdet/types/perf';

type Presentation = { label: string; color: TagColor };

const VERDICTS: Record<PerfVerdict, Presentation> = {
  slower: { label: 'SLOWER', color: 'red' },
  faster: { label: 'Faster', color: 'green' },
  no_change: { label: 'No change', color: 'gray' },
};

export const verdictPresentation = (verdict: PerfVerdict): Presentation => VERDICTS[verdict];

const STATUSES: Record<PerfRunStatus, Presentation> = {
  queued: { label: 'Queued', color: 'gray' },
  running: { label: 'Running', color: 'blue' },
  completed: { label: 'Completed', color: 'green' },
  failed: { label: 'Failed', color: 'red' },
};

export const statusPresentation = (status: PerfRunStatus): Presentation => STATUSES[status];

/** A run's verdict badge; a run without a verdict (not finished, or failed) shows its status. */
export const runVerdictPresentation = (run: { verdict: PerfVerdict | null; status: PerfRunStatus }): Presentation =>
  run.verdict ? VERDICTS[run.verdict] : STATUSES[run.status];

/** Gated rows first, then the rest, each keeping the result's order. */
export const sortRows = (rows: PerfRow[]): PerfRow[] => [...rows.filter((r) => r.gated), ...rows.filter((r) => !r.gated)];

/**
 * The gated delta that matters most: a SLOWER one if any, else the largest by magnitude.
 * Relative deltas are compared only with each other; absolute ones (ms, points) only when
 * no relative delta exists, since the two are not comparable.
 */
export function largestGatedDelta(deltas: PerfRunHeadlineDelta[]): PerfRunHeadlineDelta | null {
  const relative = deltas.filter((d) => !d.absolute);
  const comparable = relative.length > 0 ? relative : deltas;
  const slower = comparable.filter((d) => d.verdict === 'slower');
  const pool = slower.length > 0 ? slower : comparable;
  return pool.reduce<PerfRunHeadlineDelta | null>(
    (best, d) => (!best || Math.abs(d.deltaPct) > Math.abs(best.deltaPct) ? d : best),
    null
  );
}

/** Same sign convention as the verdict table: rps is shown flipped. */
export function formatHeadlineDelta(delta: PerfRunHeadlineDelta): string {
  const value = delta.unit === 'rps' ? -delta.deltaPct : delta.deltaPct;
  if (delta.unit === 'ratio') return `${signed(value, 1)} pt`;
  if (delta.absolute) return `${signed(value, 0)}${unitSuffix(delta.unit)}`;
  return `${signed(value * 100, 1)}%`;
}
